/**
 * Hook personalizado para obtener usuarios cercanos usando la RPC users_in_radius.
 * Centraliza la lógica de obtención de usuarios basados en ubicación geográfica.
 * 
 * @param {Object} user - Usuario autenticado
 * @param {Object} location - Objeto con coordenadas { lat, lng }
 * @returns {Object} - Resultado de la query con datos, loading, error y refetch
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export const useNearbyUsers = (user, location) => {
  return useQuery({
    /**
     * Query key única que incluye usuario y ubicación
     * Permite caché diferenciada por ubicación
     */
    queryKey: ['nearbyUsers', user?.id, location],
    
    /**
     * Solo ejecutar si hay usuario y ubicación
     */
    enabled: !!user?.id && !!location?.lat && !!location?.lng,
    
    /**
     * Función para obtener usuarios cercanos
     */
    queryFn: async () => {
      if (!user?.id || !location?.lat || !location?.lng) {
        throw new Error('Usuario o ubicación no disponibles');
      }

      try {
        console.log('[useNearbyUsers] Llamando a RPC users_in_radius con:', {
          userId: user.id,
          location
        });

        const { data, error } = await supabase.rpc('users_in_radius', {
          mipersonaluid: user.id
        });

        if (error) throw error;

        console.log('[useNearbyUsers] Usuarios cercanos obtenidos:', data?.length || 0);
        // console.log('[useNearbyUsers] Datos crudos de RPC:', JSON.stringify(data, null, 2));
        
        // Mapear user_id a id para mantener compatibilidad con componentes existentes
        const mappedData = (data || []).map(user => ({
          ...user,
          id: user.user_id, // Mapear user_id a id
        }));

        return mappedData;
      } catch (err) {
        console.error('[useNearbyUsers] Error al obtener usuarios cercanos:', err);
        throw err;
      }
    },
    
    /**
     * Tiempo de caché: 1 hora
     * Los usuarios cercanos no cambian tan frecuentemente
     * Solo se refresca cuando el usuario hace click manualmente
     */
    staleTime: 60 * 60 * 1000,

    /**
     * Tiempo de caché en memoria: 1 hora
     * Mantiene datos en caché por más tiempo para mejor UX
     */
    gcTime: 60 * 60 * 1000,
    
    /**
     * No refrescar automáticamente
     * Usaremos Supabase Realtime para actualizaciones en tiempo real
     */
    refetchInterval: false,
    
    /**
     * Reintentar hasta 3 veces en caso de error
     * Con delay exponencial entre reintentos
     */
    retry: (failureCount, error) => {
      // No reintentar para errores de autenticación
      if (error?.code === 'PGRST301') {
        return false;
      }
      return failureCount < 3;
    },
    
    /**
     * Callback cuando hay error en la query
     */
    onError: (error) => {
      console.error('[useNearbyUsers] Error en query:', error);
    },
    
    /**
     * Callback cuando la query tiene éxito
     */
    onSuccess: (data) => {
      console.log('[useNearbyUsers] Query exitosa, usuarios encontrados:', data?.length || 0);
    },
  });
};
